import { useState } from 'react'
import Tooltip from './ReportTooltip'
import {
  ChartBar,
  ChartBarHorizontal,
  ChartDonut,
  ListBullets,
  Wallet
} from '@phosphor-icons/react'
import AnimatedNumber from '../../../components/AnimatedNumber'
import UbiquaTowerDialog from '../../../components/dialogs/UbiquaTowerDialog'
import {
  REPORT_DISTRIBUTION_PALETTE,
  UI_COLOR_REPORT_DISTRIBUTION_EMPTY
} from '../../../constants/uiColors'
import { formatMoney } from '../../../helpers/utils'

const distributionColor = (index) =>
  REPORT_DISTRIBUTION_PALETTE[index % REPORT_DISTRIBUTION_PALETTE.length]

/** Shared ranked distribution with optional category tabs, drilldown and donut. */
export default function ReportDistributionCard({
  title,
  description,
  groups = [],
  money = false,
  allowDonut = false
}) {
  const [group, setGroup] = useState(groups[0]?.key)
  const [view, setView] = useState('bars')
  const [open, setOpen] = useState(false)
  const [hovered, setHovered] = useState(null)
  const hasData = (g) => Object.values(g.data || {}).some((value) => Number(value) > 0)
  const selected = groups.find((g) => g.key === group && hasData(g)) || groups.find(hasData)
  const activeGroup = selected || groups.find((g) => g.key === group) || groups[0]
  const rows = Object.entries(selected?.data || {})
    .map(([label, value]) => ({ label, value: Number(value) || 0 }))
    .filter((row) => money || row.value > 0)
    .sort((a, b) => b.value - a.value)
  const total = rows.reduce((sum, row) => sum + row.value, 0)
  const used = rows.filter((row) => row.value > 0)
  const activeRow = used.find((row) => row.label === hovered)
  const animationKey = `${selected?.key}:${JSON.stringify(rows)}`
  const top = money ? rows : rows.slice(0, 6)
  const rest = rows.slice(6).reduce((sum, row) => sum + row.value, 0)
  const shown =
    !money && rest ? [...top, { label: 'Otros motivos', value: rest, other: true }] : top
  const max = Math.max(1, ...shown.map((row) => row.value))
  const format = money ? formatMoney : (n) => Math.round(n).toLocaleString('es')
  const percent = (n) => (total && n ? `${((n / total) * 100).toFixed(money ? 1 : 0)}%` : '0%')
  let offset = 0
  const list = (items) => (
    <ol key={animationKey} className="report-distribution-list app-content-enter">
      {items.map((row, index) => (
        <li key={row.label} className={row.other ? 'is-other' : row.value === 0 ? 'is-zero' : ''}>
          <span className="report-distribution-rank">{row.other ? '' : index + 1}</span>
          <span className="report-distribution-label">
            <b>{row.label}</b>
            <span className="report-bar-track">
              <span className="report-bar-fill" style={{ width: `${(row.value / max) * 100}%` }} />
            </span>
          </span>
          <span className="report-distribution-value">
            <b>{format(row.value)}</b>
            <small>{percent(row.value)}</small>
          </span>
        </li>
      ))}
    </ol>
  )
  return (
    <article
      className={`report-surface report-distribution-card${money ? '' : ' report-motives-card'}`}
    >
      <div className="report-card-heading">
        <div>
          <h3>
            <span className="report-distribution-icon">
              {money ? (
                <Wallet size={14} aria-hidden="true" />
              ) : (
                <ChartBar size={14} aria-hidden="true" />
              )}
            </span>
            {title}
          </h3>
        </div>
        {groups.length > 1 && (
          <div className="report-segment" role="group" aria-label="Tipo de visita">
            {groups.map((g) => (
              <button
                key={g.key}
                type="button"
                disabled={!hasData(g)}
                aria-pressed={selected?.key === g.key}
                onClick={() => setGroup(g.key)}
              >
                {g.label}
              </button>
            ))}
          </div>
        )}
        {allowDonut && (
          <div
            className="report-segment report-view-toggle"
            role="group"
            aria-label="Tipo de vista"
          >
            {[
              ['bars', 'Vista de barras', 'Barras'],
              ['donut', 'Vista de dona', 'Dona']
            ].map(([key, label, tip]) => (
              <Tooltip key={key} title={tip}>
                <button
                  type="button"
                  aria-label={label}
                  aria-pressed={view === key}
                  onClick={() => {
                    setHovered(null)
                    setView(key)
                  }}
                >
                  {key === 'donut' ? (
                    <ChartDonut size={16} aria-hidden="true" />
                  ) : (
                    <ChartBarHorizontal size={16} aria-hidden="true" />
                  )}
                </button>
              </Tooltip>
            ))}
          </div>
        )}
      </div>
      <p className="report-distribution-description">{description}</p>
      {total > 0 ? (
        <>
          <div className="report-distribution-total">
            <strong>
              <AnimatedNumber
                key={`${animationKey}:${view}`}
                value={total}
                format={format}
                duration={900}
              />
            </strong>
            <b>{money ? 'Total por métodos de pago' : 'Total de motivos'}</b>
            {!money && selected?.label && (
              <small>Visitas de {selected.label.toLocaleLowerCase('es')}</small>
            )}
          </div>
          {view === 'bars' ? (
            list(shown)
          ) : (
            <div key={animationKey} className="report-donut-layout app-content-enter">
              <svg
                viewBox="0 0 180 180"
                className="report-donut"
                role="img"
                aria-label={`Distribución: ${rows.map((r) => `${r.label} ${percent(r.value)}`).join(', ')}`}
              >
                <circle cx="90" cy="90" r="70" fill="none" stroke="#edf0f4" strokeWidth="26" />
                {used.map((row, index) => {
                  const length = (row.value / total) * 439.823
                  const start = offset
                  offset += length
                  return (
                    <circle
                      key={row.label}
                      cx="90"
                      cy="90"
                      r="70"
                      fill="none"
                      stroke={distributionColor(index)}
                      strokeWidth={activeRow?.label === row.label ? 34 : 26}
                      strokeDasharray={`${Math.max(0, length - (used.length > 1 ? 2 : 0))} 439.823`}
                      strokeDashoffset={-start}
                      transform="rotate(-90 90 90)"
                      className="report-donut-segment"
                      style={{ opacity: activeRow && activeRow.label !== row.label ? 0.3 : 1 }}
                      onMouseEnter={() => setHovered(row.label)}
                      onMouseLeave={() => setHovered(null)}
                    />
                  )
                })}
                <text x="90" y="84" textAnchor="middle">
                  {activeRow ? percent(activeRow.value) : `${used.length} de ${rows.length}`}
                </text>
                <text x="90" y="104" textAnchor="middle" className="report-donut-caption">
                  {activeRow ? activeRow.label : 'métodos usados'}
                </text>
              </svg>
              <ul className="report-donut-legend">
                {rows.map((row, index) => (
                  <li key={row.label}>
                    <button
                      type="button"
                      disabled={!row.value}
                      className={activeRow?.label === row.label ? 'is-highlighted' : ''}
                      style={{ opacity: activeRow && activeRow.label !== row.label ? 0.45 : 1 }}
                      onFocus={() => setHovered(row.label)}
                      onBlur={() => setHovered(null)}
                      onMouseEnter={() => setHovered(row.label)}
                      onMouseLeave={() => setHovered(null)}
                    >
                      <i
                        style={{
                          background: row.value
                            ? distributionColor(index)
                            : UI_COLOR_REPORT_DISTRIBUTION_EMPTY
                        }}
                      />
                      <span>{row.label}</span>
                      <b>{format(row.value)}</b>
                      <small>{percent(row.value)}</small>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <footer>
            {money
              ? `${used.length} de ${rows.length} métodos con cobros en el periodo`
              : `Mostrando ${Math.min(6, used.length)} de ${used.length} motivos`}
            {!money && (
              <button type="button" onClick={() => setOpen(true)}>
                Ver todos
              </button>
            )}
          </footer>
        </>
      ) : (
        <div className="report-panel-empty">
          <span className="report-panel-empty-icon" aria-hidden="true">
            {money ? <Wallet size={25} /> : <ChartBar size={25} />}
          </span>
          <strong>{money ? 'Sin cobros en el periodo' : 'Sin motivos de no visita'}</strong>
          <p>
            {money
              ? 'Cuando tus usuarios registren cobros, verás aquí el monto por método de pago.'
              : `No hay motivos registrados${activeGroup?.label ? ` en visitas de ${activeGroup.label.toLocaleLowerCase('es')}` : ''} para este periodo.`}
          </p>
        </div>
      )}
      <UbiquaTowerDialog
        open={open}
        onClose={() => setOpen(false)}
        title={`${title}${selected?.label ? ` - ${selected.label}` : ''}`}
        subtitle={`${selected?.label ? `Visitas de ${selected.label.toLocaleLowerCase('es')} · ` : ''}${format(total)} motivos en el periodo`}
        icon={<ListBullets size={18} weight="bold" aria-hidden="true" />}
        paperClassName="report-motives-dialog"
        footer={
          <div className="report-motives-dialog__footer">
            Mostrando {used.length} de {used.length} motivos
          </div>
        }
      >
        <ul className="report-motives-dialog__list">
          {used.map((row) => (
            <li key={row.label}>
              <span>{row.label}</span>
              <span className="report-motives-dialog__value">
                {format(row.value)}
                <small>{percent(row.value)}</small>
              </span>
            </li>
          ))}
        </ul>
      </UbiquaTowerDialog>
    </article>
  )
}
